import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { fetchAllFeedbacks, updateFeedbackStatus, deleteFeedback } from '../services/feedback';

const Feedbacks = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const navigate = useNavigate();

    const loadFeedbacks = async () => {
        try {
            setLoading(true);
            const data = await fetchAllFeedbacks();
            if (Array.isArray(data)) {
                setFeedbacks(data);
            }
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFeedbacks();
    }, []);

    const handleStatusUpdate = async (id, status) => {
        try {
            await updateFeedbackStatus(id, status);
            loadFeedbacks();
        } catch (err) {
            alert('Failed to update status');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this feedback?')) return;
        try {
            await deleteFeedback(id);
            loadFeedbacks();
        } catch (err) {
            alert('Failed to delete feedback');
        }
    };

    const filteredFeedbacks = feedbacks.filter((f) => {
        if (filter === 'all') return true;
        return f.status === filter;
    });

    const statusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
            approved: 'bg-green-100 text-green-700 border-green-200',
            rejected: 'bg-red-100 text-red-700 border-red-200',
        };
        return (
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${styles[status] || ''}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[100vh]">
                <img src="/loading.gif" alt="Loading..." className="w-30 h-30 object-contain" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col justify-center items-center min-h-[100vh]">
                <img src="/empty.png" alt="Error occurred" className="w-64 h-auto object-contain mb-4" />
                <p className="text-red-500 font-medium">Error: {error}</p>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <div className="mb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border bg-gray-300 border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-400 transition"
                >
                    <ArrowLeft size={18} />
                    Back
                </button>
            </div>

            <h1 className="text-xl font-semibold text-gray-800 mb-4">
                User Feedbacks
            </h1>

            {/* Filter tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
                {['all', 'pending', 'approved', 'rejected'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setFilter(tab)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === tab
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600'
                            }`}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        <span className="ml-1.5 text-xs opacity-70">
                            ({tab === 'all' ? feedbacks.length : feedbacks.filter(f => f.status === tab).length})
                        </span>
                    </button>
                ))}
            </div>

            {/* Table */}
            <div className="w-full overflow-hidden border border-gray-200 rounded-lg">
                <div className="w-full overflow-x-auto">
                    <table className="min-w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-gray-100 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 font-semibold text-gray-600">User</th>
                                <th className="px-4 py-3 font-semibold text-gray-600">Role</th>
                                <th className="px-4 py-3 font-semibold text-gray-600">Rating</th>
                                <th className="px-4 py-3 font-semibold text-gray-600 min-w-[250px]">Feedback</th>
                                <th className="px-4 py-3 font-semibold text-gray-600">Status</th>
                                <th className="px-4 py-3 font-semibold text-gray-600">Date</th>
                                <th className="px-4 py-3 font-semibold text-gray-600 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredFeedbacks.map((feedback) => (
                                <tr key={feedback._id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-gray-900">
                                        <div className="flex items-center gap-2">
                                            {feedback.image ? (
                                                <img
                                                    src={feedback.image}
                                                    alt={feedback.name}
                                                    className="w-8 h-8 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                                                    {feedback.name ? feedback.name[0].toUpperCase() : '?'}
                                                </div>
                                            )}
                                            <span className="font-medium">{feedback.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-900">{feedback.role}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-0.5">
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className={`w-3.5 h-3.5 ${i < feedback.rating
                                                        ? 'fill-yellow-400 text-yellow-400'
                                                        : 'fill-gray-200 text-gray-200'
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-900 whitespace-normal min-w-[300px]">
                                        {feedback.text}
                                    </td>
                                    <td className="px-4 py-3">{statusBadge(feedback.status)}</td>
                                    <td className="px-4 py-3 text-gray-900">
                                        {new Date(feedback.createdAt).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-center gap-2">
                                            {feedback.status !== 'approved' && (
                                                <button
                                                    onClick={() => handleStatusUpdate(feedback._id, 'approved')}
                                                    className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                                                    title="Approve"
                                                >
                                                    <CheckCircle className="w-4 h-4" />
                                                </button>
                                            )}
                                            {feedback.status !== 'rejected' && (
                                                <button
                                                    onClick={() => handleStatusUpdate(feedback._id, 'rejected')}
                                                    className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                                    title="Reject"
                                                >
                                                    <XCircle className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDelete(feedback._id)}
                                                className="p-1.5 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-200 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredFeedbacks.length === 0 && (
                        <div className="p-6 text-center text-gray-500">
                            No feedbacks found.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Feedbacks;